"use client";

import type useLifestyleFilters from "../../hooks/useLifestyleFilters";
import type { PrizeCategory } from "../../lifestyle-constants";
import { CloseIcon } from "../lifestyleIcons";
import { clearFilterScreen, filterScreensFor, selectionCountFor } from "./filterScreens";

interface FilterChipsProps {
  category: PrizeCategory;
  filters: ReturnType<typeof useLifestyleFilters>;
}

/**
 * A chip per filter holding a selection, sitting under the drawer header on
 * every screen within a category.
 *
 * The X drops that whole filter, so a user three levels deep can undo one
 * group without navigating back to find it — the finer-grained way out is
 * still to open the filter and untick individual options.
 */
export default function FilterChips({ category, filters }: FilterChipsProps) {
  const active = filterScreensFor(category, filters)
    .map(screen => ({ screen, count: selectionCountFor(screen.key, filters) }))
    .filter(entry => entry.count > 0);

  if (active.length === 0) return null;

  return (
    <div className="flex items-center shrink-0 w-full px-4 py-2 gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {active.map(({ screen, count }) => (
        <div
          key={screen.key}
          className="flex items-center shrink-0 pl-2 gap-1 bg-transparent border border-black/8 rounded">
          <span className="flex items-center gap-1 text-xs font-roboto font-medium leading-4 text-secondary">
            {screen.label}

            <span className="flex items-center justify-center min-w-4 px-1 py-px text-[11px] font-jost font-semibold leading-3.5 text-dark-1 bg-[#e7e7e9] rounded-full">
              {count}
            </span>
          </span>

          <button
            type="button"
            onClick={() => clearFilterScreen(screen.key, filters)}
            aria-label={`Clear ${screen.label} filter`}
            className="flex items-center justify-center shrink-0 size-7 text-gray-5 hover:text-dark-1 transition-colors">
            <CloseIcon className="size-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
